<template>
	<div class="list-product" :class="{incart: quantity > 0, multi, disable}" @click="clickAdd">
		<div class="product-name-container">
			<div v-if="multi" class="product-quantity">{{ "x"+quantity }}</div>
			<div v-if="!multi" class="product-selected"><div class="check"><div class="check-center"></div></div></div>
			<div class="product-name">{{ finalProductName }}</div>
		</div>
		<div class="product-price-container">
			<div class="product-price" v-show="product.product_price > 0" :class="{truncate: currencyStore.dbToActualValue(finalProductPrice) >= 10000, small: currencyStore.dbToActualValue(finalProductPrice) >= 100}">{{ "+ "+currencyStore.readableCurrencyValue(finalProductPrice) }}</div>
			<div class="item-delete" @click.stop="counterStore.removeProduct(product)">
				<img :src="'https://img.qromo.io/img/icons/icon-bin-counter-blue'+(userStore.darkMode ? '-dark' : '')+'.svg'">
			</div>
		</div>
		<AvailabilityDot v-if="showAvailabilityType" :type="availabilityType" />
	</div>
</template>

<script>
import {useCurrencyStore, useLanguageStore, useSvgStore} from "@/shared/stores";
import {useCounterStore, useMenuStore, useUserStore, useWarehouseStore} from "@/console-new/stores";
import AvailabilityDot from "@/console-new/components/config/menu/AvailabilityDot.vue";

export default {
	components: {AvailabilityDot},
	setup() {
		const languageStore = useLanguageStore();
		const svgStore = useSvgStore();
		const counterStore = useCounterStore();
		const menuStore = useMenuStore();
		const userStore = useUserStore();
		const currencyStore = useCurrencyStore();
		const warehouseStore = useWarehouseStore();
		return {getString: languageStore.getString, menuStore, currencyStore, warehouseStore, svgStore, counterStore, userStore}
	},
	emits: ['toggle'],
	name: "CompositeListProduct",
	props: {
		multi: {
			type: Boolean,
			default: false
		},
		disable: {
			type: Boolean,
			default: false
		},
		product: {
			type: Object,
			required: true,
			validator: (listItem) => {
				return listItem.hasOwnProperty('product_name') && listItem.hasOwnProperty('product_price')
			}
		},
	},
	computed: {
		linkedProduct() {
			if(!!this.product.meta_product || !this.product.menu_item_linked_id) {
				return null;
			}
			return this.menuStore.modItemFromId(this.product.menu_item_linked_id);
		},
		finalProductName() {
			if(!this.linkedProduct || !this.product.linked_name) {
				return this.product.product_name;
			}
			return this.linkedProduct ? this.linkedProduct.name : this.product.product_name;
		},
		finalProductPrice() {
			if(!this.linkedProduct || !this.product.linked_price) {
				return this.product.product_price;
			}
			return this.linkedProduct ? this.linkedProduct.price : this.product.product_price;
		},
		quantity() {
			return this.counterStore.getProductQuantity(this.product);
		},
		isInWarehouse() {
			return this.linkedProduct && this.linkedProduct?.warehouse && this.linkedProduct?.sku;
		},
		showAvailabilityType() {
			return !this.product.available || (this.isInWarehouse && this.warehouseStock <= this.warehouseThreshold);
		},
		availabilityType() {
			if(this.isInWarehouse && this.warehouseStock <= this.warehouseThreshold) {
				return 1;
			}
			if(this.product.available === false) {
				return 1;
			}
			return 2;
		},
		warehouseThreshold() {
			if(this.isInWarehouse) {
				let warehouseItem = this.warehouseStore.warehouseItemsSKUs[this.linkedProduct.sku];
				if(warehouseItem) {
					return Number(warehouseItem.affects_availability || 0);
				}
			}
			return 0;
		},
		warehouseStock() {
			if(this.isInWarehouse) {
				let warehouseItem = this.warehouseStore.warehouseItemsSKUs[this.linkedProduct.sku];
				if(warehouseItem) {
					return Number(warehouseItem.stock || 0);
				}
			}
			return 0;
		}
	},
	methods: {
		clickAdd() {
			if(!this.multi && this.quantity > 0) {
				this.counterStore.removeProduct(this.product);
				return;
			}
			if(!this.disable) {
				this.counterStore.addProduct(this.product);
				this.$emit('toggle', this.product);
			}
		}
	}
}
</script>

<style scoped lang="scss">
.list-product {
	width: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 10px;
	cursor: pointer;
	box-sizing: border-box;
	box-shadow: 0 2px 3px var(--color-box-shadow-3);
	border: none;
	border-radius: 13px;
	transition: background-color 0.3s $transition, box-shadow 0.3s $transition;
	min-height: 53px;
	background-color: var(--color-bg);
	position: relative;

	* {
		user-select: none;
	}
	.availability-dot {
		position: absolute;
		top: 5px;
		right: 5px;
		pointer-events: none;
	}
	.product-name-container {
		display: flex;
		flex-direction: row;
		justify-content: flex-start;
		align-items: center;
		width: calc(100% - 110px);

		.product-quantity, .product-selected {
			max-width: 0;
			height: 100%;
			transition: max-width 0.3s $transition, width 0.3s $transition, padding-right 0.3s $transition, opacity 0.3s $transition;
			font-size: 14px;
			font-weight: 700;
			color: $primary;
			opacity: 0;
			padding-right: 0;
		}

		.product-selected {
			@include centered;
			.check {
				@include centered;
				pointer-events: none;
				border-radius: 50%;
				width: 0;
				height: 0;
				opacity: .6;
				border: 2px solid var(--color-primary);
				transition: opacity 0.3s $transition 50ms, width 0.3s $transition 50ms, height 0.3s $transition 50ms;

				.check-center {
					opacity: 0;
					width: 0;
					height: 0;
					background-color: var(--color-primary);
					border-radius: 50%;
					transition: height 0.3s $transition 50ms, width 0.3s $transition 50ms, opacity 0.3s $transition 50ms;
				}
			}
		}

		.product-name {
			font-size: 14px;
			@include truncateString(2);
		}
	}

	.product-price-container {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		width: 110px;

		.product-price {
			font-size: 14px;
			color: $primary;

			&.small {
				font-size: 12px;
			}
		}

		.item-delete {
			width: 0;
			height: 100%;
			transition: width 0.3s $transition, opacity 0.3s $transition;
			overflow: hidden;
			display: flex;
			justify-content: center;
			align-items: center;
			opacity: 0;
			padding-left: 5px;
			box-sizing: content-box;
			cursor: pointer;

			img {
				width: 100%;
				padding: 8px;
			}
		}
	}

	&.incart {
		box-shadow: inset 0 2px 3px var(--color-box-shadow-3);
		background-color: var(--color-alpha-2);

		.product-price-container {
			.item-delete {
				width: 30px;
				opacity: 1;
			}
		}

		.product-name-container {
			.product-quantity {
				max-width: 45px;
				opacity: 1;
				padding-right: 8px;
			}
			.product-selected {
				max-width: 45px;
				opacity: 1;
				padding-right: 8px;

				.check{
					opacity: 1;
					height: 16px;
					width: 16px;
					transition: opacity 0.3s $transition, width 0.3s $transition, height 0.3s $transition;

					.check-center {
						opacity: 1;
						width: 7px;
						height: 7px;
						transition: height 0.3s $transition, width 0.3s $transition, opacity 0.3s $transition;
					}
				}
			}
		}
	}

	&.disable {
		cursor: default;

		&:not(.incart) {
			opacity: .4;
		}
	}

}
</style>